import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AppLayout } from "./components/AppLayout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { PaywallGate } from "./components/PaywallGate";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Onboarding from "./pages/Onboarding";
import Projects from "./pages/Projects";
import Tasks from "./pages/Tasks";
import Calendar from "./pages/Calendar";
import Coach from "./pages/Coach";
import Releases from "./pages/Releases";
import ReleaseDetail from "./pages/ReleaseDetail";
import Autopilot from "./pages/Autopilot";import Progression from "./pages/Progression";
import Assistant from "./pages/Assistant";
import Analytics from "./pages/Analytics";
import ContentStudio from "./pages/ContentStudio";
import Publish from "./pages/Publish";
import { FeatureLock } from "./components/FeatureLock";
import Growth from "./pages/Growth";
import Connectors from "./pages/Connectors";
import { CelebrationOverlay } from "./components/CelebrationOverlay";
import Profile from "./pages/Profile";
import ManagerDashboard from "./pages/ManagerDashboard";
import ManagerArtists from "./pages/ManagerArtists";
import ManagerArtistDetail from "./pages/ManagerArtistDetail";
import ManagerInvitations from "./pages/ManagerInvitations";
import BecomeManager from "./pages/BecomeManager";
import InviteAccept from "./pages/InviteAccept";
import { AdminLayout } from "./components/AdminLayout";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUsers from "./pages/AdminUsers";
import AdminPlans from "./pages/AdminPlans";
import AdminContent from "./pages/AdminContent";
import AdminAudit from "./pages/AdminAudit";
import AdminMarketplace from "./pages/AdminMarketplace";
import Marketplace from "./pages/Marketplace";
import ProfessionalDetail from "./pages/ProfessionalDetail";
import ProfessionalSetup from "./pages/ProfessionalSetup";
import ProjectRequestDetail from "./pages/ProjectRequestDetail";
import Messages from "./pages/Messages";
import Orders from "./pages/Orders";
import AdminCatalog from "./pages/AdminCatalog";
import AdminLogin from "./pages/AdminLogin";
import AdminUserDetail from "./pages/AdminUserDetail";
import AdminGamification from "./pages/AdminGamification";
import AdminAI from "./pages/AdminAI";
import AdminDisputes from "./pages/AdminDisputes";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminFunnel from "./pages/AdminFunnel";
import PaymentResult from "./pages/PaymentResult";
import AuthCallback from "./pages/AuthCallback";
import GoogleAuth from "./pages/GoogleAuth";
import DashboardRouter from "./components/DashboardRouter";
import ProDemandes from "./pages/pro/ProDemandes";
import ProPropositions from "./pages/pro/ProPropositions";
import ProDevis from "./pages/pro/ProDevis";
import ProCommandes from "./pages/pro/ProCommandes";
import ProReservations from "./pages/pro/ProReservations";
import ProFactures from "./pages/pro/ProFactures";
import ManagerDiscover from "./pages/ManagerDiscover";
import ManagerArtistPublic from "./pages/ManagerArtistPublic";
import ManagerCommerce from "./pages/ManagerCommerce";
import Legal from "./pages/Legal";
import Support from "./pages/Support";
import NotFound from "./pages/NotFound";
import Subscription from "./pages/Subscription";
import Tarifs from "./pages/Tarifs";
import Landing from "./pages/Landing";
import Diagnostic from "./pages/Diagnostic";
import DiagnosticResult from "./pages/DiagnosticResult";
import Activation from "./pages/Activation";

function Shell({ children, permission = null, profile = null }) {
  return (
    <ProtectedRoute requirePermission={permission} requireProfile={profile}>
      <AppLayout>{children}</AppLayout>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-center" theme="dark" richColors />
          <PaywallGate />
          <CelebrationOverlay />
          <AuthCallback />
          <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/google" element={<GoogleAuth />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/onboarding" element={<ProtectedRoute requireOnboarded={false}><Onboarding /></ProtectedRoute>} />
            <Route path="/demarrage" element={<ProtectedRoute><Activation /></ProtectedRoute>} />
            <Route path="/dashboard" element={<Shell><DashboardRouter /></Shell>} />
            <Route path="/projects" element={<Shell permission="projects"><Projects /></Shell>} />
            <Route path="/tasks" element={<Shell permission="tasks"><Tasks /></Shell>} />
            <Route path="/calendar" element={<Shell permission="calendar"><Calendar /></Shell>} />
            <Route path="/coach" element={<Shell permission="coach"><Coach /></Shell>} />
            <Route path="/releases" element={<Shell permission="releases"><Releases /></Shell>} />
            <Route path="/releases/:id" element={<Shell permission="releases"><ReleaseDetail /></Shell>} />
            <Route path="/ai" element={<Navigate to="/coach" replace />} />
            <Route path="/content" element={<Shell><FeatureLock entitlement="CONTENT_STUDIO" feature="Content Studio" requiredPlan="PREMIUM"><ContentStudio /></FeatureLock></Shell>} />
            <Route path="/publish" element={<Shell><FeatureLock entitlement="CONTENT_STUDIO" feature="Publication multi-réseaux" requiredPlan="PREMIUM"><Publish /></FeatureLock></Shell>} />
            <Route path="/analytics" element={<Shell permission="analytics"><Analytics /></Shell>} />
            <Route path="/growth" element={<Shell permission="growth"><Growth /></Shell>} />
            <Route path="/connectors" element={<Shell permission="connectors"><Connectors /></Shell>} />
            <Route path="/autopilot" element={<Shell><FeatureLock entitlement="MARKETING_AUTOPILOT" feature="Marketing Autopilot" requiredPlan="PRO"><Autopilot /></FeatureLock></Shell>} />
            <Route path="/progression" element={<Shell><Progression /></Shell>} />
            <Route path="/assistant" element={<Shell permission="assistant"><Assistant /></Shell>} />
            <Route path="/profile" element={<Shell><Profile /></Shell>} />
            <Route path="/marketplace" element={<Shell><Marketplace /></Shell>} />
            <Route path="/marketplace/requests/:id" element={<Shell><ProjectRequestDetail /></Shell>} />
            <Route path="/marketplace/:id" element={<Shell><ProfessionalDetail /></Shell>} />
            <Route path="/pro" element={<Shell><ProfessionalSetup /></Shell>} />
            <Route path="/pro/demandes" element={<Shell><ProDemandes /></Shell>} />
            <Route path="/pro/propositions" element={<Shell><ProPropositions /></Shell>} />
            <Route path="/pro/devis" element={<Shell><ProDevis /></Shell>} />
            <Route path="/pro/commandes" element={<Shell><ProCommandes /></Shell>} />
            <Route path="/pro/reservations" element={<Shell><ProReservations /></Shell>} />
            <Route path="/pro/factures" element={<Shell><ProFactures /></Shell>} />
            <Route path="/pro/disponibilites" element={<Shell><ProfessionalSetup /></Shell>} />
            <Route path="/pro/portfolio" element={<Shell><ProfessionalSetup /></Shell>} />
            <Route path="/pro/profil" element={<Shell><ProfessionalSetup /></Shell>} />
            <Route path="/messages" element={<Shell><Messages /></Shell>} />
            <Route path="/messages/:id" element={<Shell><Messages /></Shell>} />
            <Route path="/commandes" element={<Shell><Orders /></Shell>} />
            <Route path="/manager" element={<Shell profile="MANAGER"><ManagerDashboard /></Shell>} />
            <Route path="/manager/artists" element={<Shell profile="MANAGER"><ManagerArtists /></Shell>} />
            <Route path="/manager/discover" element={<Shell profile="MANAGER"><ManagerDiscover /></Shell>} />
            <Route path="/manager/discover/:id" element={<Shell profile="MANAGER"><ManagerArtistPublic /></Shell>} />
            <Route path="/manager/matching" element={<Shell profile="MANAGER"><ManagerDiscover matchMode /></Shell>} />
            <Route path="/manager/commerce" element={<Shell profile="MANAGER"><ManagerCommerce /></Shell>} />
            <Route path="/manager/artists/:id" element={<Shell profile="MANAGER"><ManagerArtistDetail /></Shell>} />
            <Route path="/manager/invitations" element={<Shell profile="MANAGER"><ManagerInvitations /></Shell>} />
            <Route path="/become-manager" element={<Shell><BecomeManager /></Shell>} />
            <Route path="/invite/:token" element={<ProtectedRoute><InviteAccept /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute><AdminLayout><AdminUsers /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/plans" element={<ProtectedRoute><AdminLayout><AdminPlans /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/content" element={<ProtectedRoute><AdminLayout><AdminContent /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/historique" element={<ProtectedRoute><AdminLayout><AdminAudit /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/marketplace" element={<ProtectedRoute><AdminLayout><AdminMarketplace /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/catalog" element={<ProtectedRoute><AdminLayout><AdminCatalog /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/users/:id" element={<ProtectedRoute><AdminLayout><AdminUserDetail /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/gamification" element={<ProtectedRoute><AdminLayout><AdminGamification /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/ai" element={<ProtectedRoute><AdminLayout><AdminAI /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/disputes" element={<ProtectedRoute><AdminLayout><AdminDisputes /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute><AdminLayout><AdminAnalytics /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/funnel" element={<ProtectedRoute><AdminLayout><AdminFunnel /></AdminLayout></ProtectedRoute>} />
            <Route path="/subscription" element={<Shell><Subscription /></Shell>} />
            <Route path="/payment/success" element={<Shell><PaymentResult success={true} /></Shell>} />
            <Route path="/payment/cancel" element={<Shell><PaymentResult success={false} /></Shell>} />
            <Route path="/legal/cgu" element={<Legal kind="cgu" />} />
            <Route path="/legal/confidentialite" element={<Legal kind="privacy" />} />
            <Route path="/legal/mentions-legales" element={<Legal kind="mentions" />} />
            <Route path="/aide" element={<Support kind="help" />} />
            <Route path="/faq" element={<Support kind="faq" />} />
            <Route path="/contact" element={<Support kind="contact" />} />
            <Route path="/tarifs" element={<Tarifs />} />
            <Route path="/diagnostic" element={<Diagnostic />} />
            <Route path="/diagnostic/result" element={<DiagnosticResult />} />
            <Route path="/" element={<Landing />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </ErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
